import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ref, onValue } from 'firebase/database';
import { X, Database, RefreshCw } from 'lucide-react';

// The bot sends a status report (botStatus) every minute (HEALTH_SECONDS in the whatnotlive cog).
// If it stops arriving, the bot is treated as down.
const BOT_LATE_MINUTES = 3;
const BOT_DOWN_MINUTES = 10;
const RECONNECT_GRACE_MS = 20000;  // after (re)connecting or coming back to the page, wait this long for fresh data
const DB_DOWN_AFTER_MS = 30000;    // the database connection only counts as down once it's been gone this long

const LEVELS = {
  ok:      { color: '#22c55e', label: 'Operational' },
  warn:    { color: '#f59e0b', label: 'Degraded' },
  down:    { color: '#ef4444', label: 'Down' },
  pending: { color: '#71717a', label: 'Waiting' },
};
const RANK = { ok: 0, pending: 1, warn: 2, down: 3 };

// How healthy a regular check is: fine if it succeeded within ~2 of its intervals
function checkLevel(lastOk, everyMinutes, now) {
  if (!lastOk) return 'pending';
  const late = (now - lastOk) / 60000;
  if (late <= everyMinutes * 2 + 2) return 'ok';
  if (late <= everyMinutes * 4 + 5) return 'warn';
  return 'down';
}

// What's wrong when the bot's checks of Whatnot are failing, in plain words
function whatnotProblem(wn, now) {
  if (wn.retryAt > now) {
    const minutes = Math.ceil((wn.retryAt - now) / 60000);
    return `Whatnot is refusing requests, retrying in ${minutes} min`;
  }
  if (wn.status === 'timed out') return 'Whatnot is slow to answer, retrying';
  return "Can't reach Whatnot, retrying";
}

export function useSiteStatus(database) {
  const [report, setReport] = useState(undefined);   // undefined = not loaded yet, null = never sent
  const [botAvatar, setBotAvatar] = useState(null);  // TOGΞ's avatar, kept up to date by the bot
  const [connected, setConnected] = useState(null);
  const [mountedAt] = useState(() => Date.now());
  const [connectedAt, setConnectedAt] = useState(0);
  const [disconnectedAt, setDisconnectedAt] = useState(null);
  const [wokeAt, setWokeAt] = useState(0);              // when the page last came back into view
  const [reportAt, setReportAt] = useState(0);         // when the report last arrived
  const [, setTick] = useState(0);
  const lastSure = useRef(Date.now());                 // the last moment the data was known to be current

  useEffect(() => {
    const redraw = () => setTick((t) => t + 1);
    const offReport = onValue(ref(database, 'botStatus'), (snap) => { setReport(snap.val()); setReportAt(Date.now()); },
      () => setReport(null));
    const offAvatar = onValue(ref(database, 'discordAvatars/bot'), (snap) => setBotAvatar(snap.val() || null), () => {});
    const offConn = onValue(ref(database, '.info/connected'), (snap) => {
      const isConnected = !!snap.val();
      setConnected(isConnected);
      if (isConnected) {
        setConnectedAt(Date.now());
        setDisconnectedAt(null);
        setTimeout(redraw, RECONNECT_GRACE_MS + 500);  // judge again once the grace is over
      } else {
        setDisconnectedAt((d) => d || Date.now());
        setTimeout(redraw, DB_DOWN_AFTER_MS + 500);    // and if it's still gone by then, say so
      }
    });
    const tick = setInterval(redraw, 15000);
    const onWake = () => {
      if (document.visibilityState !== 'visible') return;
      setWokeAt(Date.now());
      setTimeout(redraw, RECONNECT_GRACE_MS + 500);
    };
    document.addEventListener('visibilitychange', onWake);
    return () => { offReport(); offAvatar(); offConn(); clearInterval(tick); document.removeEventListener('visibilitychange', onWake); };
  }, [database]);

  const rows = [];
  const now = Date.now();

  // Only a confirmed problem changes the status. A dropped connection, or coming back to the page (after sleep the
  // clock jumps ahead before the database catches up, so the last report looks hours old), isn't one: until fresh
  // data has arrived (or had RECONNECT_GRACE_MS to), everything is judged as it was at the last moment it was current.
  const since = Math.max(connectedAt, wokeAt);
  const current = connected && (reportAt >= since || now - since >= RECONNECT_GRACE_MS);
  if (current) lastSure.current = now;
  const judgeAt = current ? now : lastSure.current;

  // Discord bot: is the status report still arriving?
  const updatedAt = report && report.updatedAt;
  const age = updatedAt ? (judgeAt - updatedAt) / 60000 : Infinity;
  const botLevel = report === undefined ? 'pending'
    : age <= BOT_LATE_MINUTES ? 'ok' : age <= BOT_DOWN_MINUTES ? 'warn' : 'down';
  const botUp = botLevel === 'ok' || botLevel === 'warn';
  // Shown third, under the two Whatnot rows
  const botRow = {
    name: report && report.botName ? `${report.botName} Discord bot` : 'Discord bot',
    level: botLevel,
    icon: botAvatar || '/discord.png',
    roundIcon: !!botAvatar,
    detail: report === undefined ? 'Checking…'
      : botLevel === 'ok' ? 'Online' : botLevel === 'warn' ? 'Running late' : 'Offline',
  };
  // Plain wording for the sync rows
  const syncDetail = (level) => ({ ok: 'Connected', warn: 'Delayed', down: 'Not syncing', pending: 'Waiting for the first check' })[level];

  // Everything below comes from the bot, so it's unknown while the bot is down
  const fromBot = (name, level, detail, icon) => rows.push(botUp ? { name, level, detail, icon }
    : { name, level: 'pending', detail: report === undefined ? 'Checking…' : 'Unknown while the bot is offline', icon });

  const wn = (report && report.whatnot) || {};
  const live = (report && report.liveCheck) || {};
  const liveEvery = live.everyMinutes || 5;
  // A failed check on its own only makes it amber: the next one is a few minutes away, and it's red once that's been
  // going on long enough to matter
  const wnAgeLevel = checkLevel(wn.lastOk, liveEvery, judgeAt);
  const wnLevel = wn.failing && wnAgeLevel !== 'down' ? 'warn' : wnAgeLevel;
  // Healthy: how quickly Whatnot answered the bot's last check, e.g. "Connected · 286 ms"
  const responseTime = wnLevel === 'ok' && typeof wn.responseMs === 'number' ? ` · ${wn.responseMs.toLocaleString()} ms` : '';
  // My own stream comes from Whatnot's official API while it's working (lastOk is left out when it isn't, and the bot
  // goes back to its own checks, so this row does too)
  const api = (report && report.sellerApi) || {};
  const apiTime = typeof api.responseMs === 'number' ? ` · ${api.responseMs.toLocaleString()} ms` : '';
  const apiShows = checkLevel(api.shows && api.shows.lastOk, ((api.shows && api.shows.everySeconds) || 60) / 60, judgeAt);
  if (apiShows === 'ok') fromBot('Whatnot stream sync', 'ok', `Official API${apiTime}`, '/wnlogo.png');
  else fromBot('Whatnot stream sync', wnLevel, wn.failing ? whatnotProblem(wn, now) : syncDetail(wnLevel) + responseTime, '/wnlogo.png');

  const shop = (report && report.shopCheck) || {};
  const shopEvery = shop.everyMinutes || 15;
  const shopLevel = checkLevel(shop.lastOk, shopEvery, judgeAt);
  fromBot('Whatnot shop sync', shopLevel, syncDetail(shopLevel), '/wnlogo.png');

  // Live alerts: how many sellers the bot watches (runs on the stream checks, so it shares their health)
  const alerts = report && report.alerts;
  if (alerts && typeof alerts.tracked === 'number') {
    const n = alerts.tracked;
    fromBot('Whatnot live alerts', wnLevel,
      wn.failing ? 'Paused until Whatnot answers'
        : wnLevel === 'pending' ? syncDetail('pending')
        : `Watching ${n.toLocaleString()} seller${n !== 1 ? 's' : ''}`,
      '/wnlogo.png');
  }

  rows.push(botRow);

  // Live database: the website's own connection. A short drop (going off the page, a network blip) stays green
  const firstConnect = connected === null || (!connected && !connectedAt);
  const connecting = firstConnect && now - mountedAt < 8000;
  const blip = !connected && connectedAt && disconnectedAt && now - disconnectedAt < DB_DOWN_AFTER_MS;
  rows.push({
    name: 'Live database',
    icon: 'database',
    level: connected || blip ? 'ok' : connecting ? 'pending' : 'down',
    detail: connected || blip ? 'Connected' : connecting ? 'Connecting…' : 'Disconnected, retrying',
  });

  // Features: from the bot (an unloaded cog comes through as not ok, so it shows red)
  const features = botUp ? [...((report && report.features) || [])] : [];
  const overall = rows.reduce((worst, r) => (RANK[r.level] > RANK[worst] ? r.level : worst), 'ok');
  return { rows, features, overall, loading: report === undefined };
}

function Dot({ level, size = 8, pulse = false }) {
  const color = LEVELS[level].color;
  return (
    <span className="relative inline-flex flex-shrink-0" style={{ width: size, height: size }}>
      {pulse && <span className="absolute inset-0 rounded-full animate-ping" style={{ background: color, opacity: 0.5 }} />}
      <span className="relative rounded-full w-full h-full" style={{ background: color, boxShadow: `0 0 6px ${color}` }} />
    </span>
  );
}

export default function StatusPanel({ database }) {
  const [open, setOpen] = useState(false);
  const { rows, features, overall, loading } = useSiteStatus(database);
  const summary = loading ? 'Checking status…'
    : overall === 'ok' ? 'All systems operational'
    : overall === 'down' ? 'Something is down'
    : overall === 'warn' ? 'Some things are running slow'
    : 'Waiting for updates';

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        title={summary}
        aria-label={`Site status: ${summary}`}
        className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full px-2.5 py-1.5 transition-all hover:scale-105 active:scale-95"
      >
        <Dot level={loading ? 'pending' : overall} pulse={!loading && overall === 'ok'} />
        <span className="hidden sm:inline text-[11px] font-medium text-zinc-300 tracking-wide">Status</span>
        {/* dotted "i", so it's clear the pill can be clicked */}
        <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2" strokeLinecap="round" className="text-zinc-400 flex-shrink-0">
          <circle cx="12" cy="12" r="10" strokeDasharray="0.01 3.93" strokeWidth="2.4" />
          <line x1="12" y1="11" x2="12" y2="16.5" />
          <circle cx="12" cy="7.5" r="0.4" fill="currentColor" strokeWidth="2.2" />
        </svg>
      </button>

      {open && createPortal(
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }} onClick={() => setOpen(false)}>
          <div className="bg-zinc-900 border border-zinc-700/70 rounded-2xl shadow-2xl w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <span className="text-white font-bold text-base">System Status</span>
              <button onClick={() => setOpen(false)} className="text-zinc-400 hover:text-white transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex items-center gap-2.5 rounded-xl px-3 py-2.5 mb-4" style={{ background: `${LEVELS[loading ? 'pending' : overall].color}1a`, border: `1px solid ${LEVELS[loading ? 'pending' : overall].color}40` }}>
              <Dot level={loading ? 'pending' : overall} size={10} pulse={!loading && overall === 'ok'} />
              <span className="text-white text-sm font-semibold">{summary}</span>
            </div>

            <div className="flex flex-col divide-y divide-zinc-800">
              {rows.map((r) => (
                <div key={r.name} className="flex items-start gap-3 py-2.5">
                  <span className="mt-1.5"><Dot level={r.level} /></span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="flex items-center gap-1.5 text-white text-sm font-medium">
                        {r.icon === 'database' ? (
                          // a tile the same size and shape as the Discord and Whatnot logos
                          <span className="flex-shrink-0 flex items-center justify-center" style={{ width: '14px', height: '14px', borderRadius: '4px', background: 'linear-gradient(to bottom, #ffb340, #f57c00)' /* Firebase orange */ }}>
                            <Database className="text-white" style={{ width: '9px', height: '9px' }} strokeWidth={2.75} />
                          </span>
                        ) : r.icon && <img src={r.icon} alt="" className={`flex-shrink-0 ${r.roundIcon ? 'rounded-full object-cover' : 'object-contain'}`} style={{ width: '14px', height: '14px' }} />}
                        {r.name}
                      </span>
                      <span className="text-[11px] font-semibold" style={{ color: LEVELS[r.level].color }}>{LEVELS[r.level].label}</span>
                    </div>
                    <div className="text-zinc-400 text-xs mt-0.5">{r.detail}</div>
                  </div>
                </div>
              ))}
            </div>

            {features.length > 0 && (
              <div className="mt-3 pt-3 border-t border-zinc-800">
                <div className="text-zinc-500 text-[10px] font-semibold uppercase tracking-wider mb-2 text-center">Bot features</div>
                <div className="flex flex-wrap justify-center gap-1.5">
                  {features.map((f) => (
                    <span key={f.name} className="flex items-center gap-1.5 bg-zinc-800/80 rounded-full px-2 py-0.5 text-[11px] text-zinc-300">
                      <Dot level={f.ok ? 'ok' : 'down'} size={6} />
                      {f.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center justify-center gap-1 text-zinc-600 text-[10px] mt-4">
              <RefreshCw style={{ width: '10px', height: '10px' }} strokeWidth={2.5} />
              Updates in real-time
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
